import { use, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { sessionState, SIGNED_OUT_PATH } from '@/client/session';

/** The auth boundary (WP-02, SEC-AUTH).
 *
 *  Everything private is inside this; `/signin`, `/card/:token` and `/portal` are outside it. The rule it enforces is
 *  narrow and worth stating exactly: **an unauthenticated visitor must not be able to make the app request a private
 *  dataset.** Before this existed the request went out at import time, so there was no moment at which a guard could
 *  have run — the fetch preceded the router.
 *
 *  The guard is not the security control; the server's kernel cascade is, and it refuses every one of these reads
 *  without a session. This is the control that stops a signed-out person being shown a spinner and then an error
 *  wall, and it is what makes `returnTo` possible. Both matter, and neither is a substitute for the other.
 */
export function RequireSession({ children }: { children: ReactNode }) {
  const { session, unguarded } = use(sessionState());
  const location = useLocation();

  // The in-process sim has no server and no session to have. Gating it would put a sign-in wall in front of a
  // dataset that lives in the same process, and would break every design-fidelity capture for no security gain.
  if (unguarded || session) return <>{children}</>;

  const next = `${location.pathname}${location.search}`;
  return <Navigate to={`${SIGNED_OUT_PATH}?next=${encodeURIComponent(next)}`} replace />;
}
